import { useEffect, useMemo, useState } from "react";
import { pageIndex, redirects } from "./generated/page-index";

type PageIndex = (typeof pageIndex)[number];
type Page = PageIndex & {
  bodyClass: string;
  head: string[];
  body: string;
  scripts: {
    src: string;
    content: string;
    type: string;
    id: string;
    attrs: string;
  }[];
};

const managedSelector = "[data-cod5-static-managed='true']";

function normalizePath(pathname: string): string {
  if (!pathname || pathname === "") return "/";
  if (!pathname.endsWith("/")) return `${pathname}/`;
  return pathname;
}

function findPageIndex(pathname: string): PageIndex {
  const normalized = normalizePath(pathname);
  const redirect = redirects.find((item) => item.from === normalized || item.from === pathname);
  if (redirect) {
    window.history.replaceState(null, "", redirect.to);
    return findPageIndex(redirect.to);
  }
  return pageIndex.find((page) => page.path === normalized) ?? pageIndex[0];
}

function rewriteCanonical(canonical: string): string {
  if (!canonical) return window.location.href;
  if (canonical.startsWith("http")) return canonical;
  return `${window.location.origin}${canonical}`;
}

function isStaticInternalHost(hostname: string): boolean {
  return hostname === window.location.hostname || hostname === "clinicapetterle.com.br";
}

function scrollToHash(hash: string): void {
  if (!hash) return;
  const id = decodeURIComponent(hash.replace(/^#/, ""));
  const target = document.getElementById(id);
  if (!target) return;
  window.requestAnimationFrame(() => {
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  });
}

function managedFromHtml(html: string): HTMLElement[] {
  const template = document.createElement("template");
  template.innerHTML = html.trim();
  return Array.from(template.content.children).map((node) => {
    const element = node as HTMLElement;
    element.dataset.cod5StaticManaged = "true";
    return element;
  });
}

function setHead(page: Page): void {
  document.head.querySelectorAll(managedSelector).forEach((element) => element.remove());
  document.title = page.title || "Clínica Petterle";

  const sourceCanonical = document.head.querySelector<HTMLLinkElement>(
    "link[rel='canonical'][data-cod5-source-canonical='true']"
  );
  if (sourceCanonical) {
    sourceCanonical.href = rewriteCanonical(page.canonical);
  } else {
    const canonical = document.createElement("link");
    canonical.rel = "canonical";
    canonical.href = rewriteCanonical(page.canonical);
    canonical.dataset.cod5StaticManaged = "true";
    document.head.appendChild(canonical);
  }

  for (const item of page.head) {
    for (const element of managedFromHtml(item)) {
      if (element.tagName === "TITLE") continue;
      if (
        element.tagName === "LINK" &&
        (element as HTMLLinkElement).rel === "canonical"
      ) {
        continue;
      }
      document.head.appendChild(element);
    }
  }

  if (page.description && !document.head.querySelector(`${managedSelector}[name='description']`)) {
    const description = document.createElement("meta");
    description.name = "description";
    description.content = page.description;
    description.dataset.cod5StaticManaged = "true";
    document.head.appendChild(description);
  }
}

function setBodyClass(page: Page): () => void {
  const previousClass = document.body.className;
  document.body.className = page.bodyClass || "";
  document.body.classList.add("cod5-static-body");
  return () => {
    document.body.className = previousClass;
  };
}

async function runScripts(page: Page): Promise<void> {
  document.body.querySelectorAll(managedSelector).forEach((element) => {
    if (element.tagName === "SCRIPT") element.remove();
  });

  for (const scriptData of page.scripts) {
    await new Promise<void>((resolve) => {
      const script = document.createElement("script");
      script.dataset.cod5StaticManaged = "true";
      if (scriptData.id) script.id = scriptData.id;
      if (scriptData.type) script.type = scriptData.type;
      if (scriptData.src) {
        script.src = scriptData.src;
        script.async = false;
        script.onload = () => resolve();
        script.onerror = () => resolve();
      } else {
        script.text = scriptData.content;
      }
      document.body.appendChild(script);
      if (!scriptData.src) resolve();
    });
  }

  window.dispatchEvent(new Event("load"));
  window.dispatchEvent(new Event("resize"));
}

function animationFromSettings(element: Element): string {
  const raw = element.getAttribute("data-settings");
  if (!raw) return "fadeIn";
  try {
    const settings = JSON.parse(raw.replaceAll("&quot;", "\""));
    return (
      settings._animation ||
      settings.animation ||
      settings._animation_mobile ||
      settings._animation_tablet ||
      "fadeIn"
    );
  } catch {
    return "fadeIn";
  }
}

function activateStaticMotion(): () => void {
  const targets = Array.from(document.querySelectorAll(".elementor-invisible"));
  if (!targets.length) return () => undefined;

  const reveal = (element: Element) => {
    element.classList.remove("elementor-invisible");
    element.classList.add("animated", animationFromSettings(element));
  };

  if (!("IntersectionObserver" in window)) {
    targets.forEach(reveal);
    return () => undefined;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          reveal(entry.target);
          observer.unobserve(entry.target);
        }
      }
    },
    { rootMargin: "0px 0px -8% 0px", threshold: 0.08 }
  );

  targets.forEach((element) => observer.observe(element));
  window.setTimeout(() => {
    document.querySelectorAll(".elementor-invisible").forEach(reveal);
  }, 1800);

  return () => observer.disconnect();
}

function activateStaticCounters(): void {
  document.querySelectorAll<HTMLElement>(".elementor-counter-number").forEach((element) => {
    const value =
      element.dataset.toValue ||
      element.getAttribute("data-to-value") ||
      element.getAttribute("data-to") ||
      element.textContent ||
      "";
    const delimiter = element.dataset.delimiter || element.getAttribute("data-delimiter") || ",";
    if (!value) return;
    const normalized = value.replace(/\B(?=(\d{3})+(?!\d))/g, delimiter);
    element.textContent = normalized;
  });
}

function activateLazyAssets(): void {
  document.querySelectorAll<HTMLImageElement>("img[data-lazy-src]").forEach((image) => {
    const lazySrc = image.dataset.lazySrc;
    if (lazySrc && (!image.currentSrc || image.currentSrc.startsWith("data:image/svg+xml"))) {
      image.src = lazySrc;
    }
    if (image.dataset.lazySrcset) image.srcset = image.dataset.lazySrcset;
  });

  document.querySelectorAll<HTMLImageElement>("img.swiper-lazy[data-src], img[data-src]").forEach((image) => {
    const src = image.dataset.src;
    if (src && !image.currentSrc) image.src = src;
    image.classList.remove("swiper-lazy");
    image.classList.add("swiper-lazy-loaded");
  });

  document.querySelectorAll<HTMLElement>("[data-wpr-lazyrender]").forEach((element) => {
    element.removeAttribute("data-wpr-lazyrender");
  });

  document.querySelectorAll<HTMLElement>(".swiper-lazy-preloader").forEach((element) => {
    element.remove();
  });
}

function normalizeStaticLinks(): void {
  document.querySelectorAll<HTMLAnchorElement>("a[href]").forEach((anchor) => {
    const rawHref = anchor.getAttribute("href");
    if (!rawHref || rawHref.startsWith("#") || rawHref.startsWith("mailto:") || rawHref.startsWith("tel:")) {
      return;
    }

    try {
      const url = new URL(rawHref, window.location.origin);
      if (!isStaticInternalHost(url.hostname)) return;

      const path = normalizePath(url.pathname);
      const hasRoute =
        pageIndex.some((item) => item.path === path) ||
        redirects.some((item) => item.from === path) ||
        path === "/";

      if (hasRoute || url.hash) {
        anchor.href = `${window.location.origin}${path}${url.hash}`;
      }
    } catch {
      // Keep malformed legacy plugin links untouched.
    }
  });
}

function activateStaticMenus(): () => void {
  const removeHandlers: Array<() => void> = [];

  document.querySelectorAll<HTMLElement>(".elementor-menu-toggle").forEach((toggle) => {
    const widget = toggle.closest<HTMLElement>(".elementor-widget-nav-menu");
    const dropdown =
      widget?.querySelector<HTMLElement>(
        ":scope > .elementor-widget-container > .elementor-nav-menu--dropdown.elementor-nav-menu__container"
      ) ??
      toggle.parentElement?.querySelector<HTMLElement>(
        ":scope > .elementor-nav-menu--dropdown.elementor-nav-menu__container"
      );

    const onToggle = () => {
      const isOpen = toggle.getAttribute("aria-expanded") === "true";
      const nextOpen = !isOpen;
      toggle.setAttribute("aria-expanded", String(!isOpen));
      toggle.classList.toggle("elementor-active", nextOpen);
      widget?.classList.toggle("cod5-static-mobile-menu-open", nextOpen);
      dropdown?.classList.toggle("cod5-static-dropdown-open", nextOpen);

      if (dropdown && nextOpen && window.matchMedia("(max-width: 1024px)").matches) {
        const rect = toggle.getBoundingClientRect();
        const top = Math.max(72, Math.round(rect.bottom + 8));
        dropdown.classList.add("cod5-static-mobile-panel");
        dropdown.style.setProperty("--cod5-menu-top", `${top}px`);
      } else if (dropdown) {
        dropdown.classList.remove("cod5-static-mobile-panel");
        dropdown.style.removeProperty("--cod5-menu-top");
      }
    };

    const onKeydown = (event: KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onToggle();
      }
    };

    toggle.addEventListener("click", onToggle);
    toggle.addEventListener("keydown", onKeydown);
    removeHandlers.push(() => {
      toggle.removeEventListener("click", onToggle);
      toggle.removeEventListener("keydown", onKeydown);
    });
  });

  document.querySelectorAll<HTMLElement>(".menu-item-has-children").forEach((item) => {
    const link = item.querySelector<HTMLAnchorElement>("a");
    const submenu = item.querySelector<HTMLElement>(".sub-menu");
    if (!link || !submenu) return;

    link.setAttribute("aria-haspopup", "true");
    link.setAttribute("aria-expanded", "false");

    const open = () => {
      link.setAttribute("aria-expanded", "true");
      item.classList.add("cod5-static-submenu-open");
    };
    const close = () => {
      link.setAttribute("aria-expanded", "false");
      item.classList.remove("cod5-static-submenu-open");
    };
    const onClick = (event: MouseEvent) => {
      if (window.matchMedia("(max-width: 1024px)").matches) {
        event.preventDefault();
        if (item.classList.contains("cod5-static-submenu-open")) close();
        else open();
      }
    };

    item.addEventListener("mouseenter", open);
    item.addEventListener("mouseleave", close);
    item.addEventListener("focusin", open);
    item.addEventListener("focusout", close);
    link.addEventListener("click", onClick);
    removeHandlers.push(() => {
      item.removeEventListener("mouseenter", open);
      item.removeEventListener("mouseleave", close);
      item.removeEventListener("focusin", open);
      item.removeEventListener("focusout", close);
      link.removeEventListener("click", onClick);
    });
  });

  return () => removeHandlers.forEach((remove) => remove());
}

function normalizeBlogImages(): void {
  const fallbackImage = "/app/uploads/2025/09/cropped-logo-clinica-petterle.png";

  document.querySelectorAll<HTMLElement>(".elementor-post__card").forEach((card) => {
    let thumbnailLink = card.querySelector<HTMLAnchorElement>(".elementor-post__thumbnail__link");
    const titleLink = card.querySelector<HTMLAnchorElement>(".elementor-post__title a");
    const title = titleLink?.textContent?.trim() || "Clínica Petterle";

    if (!thumbnailLink) {
      thumbnailLink = document.createElement("a");
      thumbnailLink.className = "elementor-post__thumbnail__link cod5-static-fallback-thumb-link";
      thumbnailLink.href = titleLink?.href || "/blog/";
      thumbnailLink.tabIndex = -1;
      card.prepend(thumbnailLink);
    }

    let thumbnail = thumbnailLink.querySelector<HTMLElement>(".elementor-post__thumbnail");
    if (!thumbnail) {
      thumbnail = document.createElement("div");
      thumbnail.className = "elementor-post__thumbnail cod5-static-fallback-thumb";
      thumbnailLink.appendChild(thumbnail);
    }

    let image = thumbnail.querySelector<HTMLImageElement>("img");
    if (!image) {
      image = document.createElement("img");
      image.src = fallbackImage;
      image.alt = title;
      image.loading = "lazy";
      image.decoding = "async";
      image.className = "cod5-static-blog-fallback-image";
      thumbnail.appendChild(image);
    }

    image.removeAttribute("srcset");
    image.removeAttribute("sizes");
    image.loading = "eager";
    image.decoding = "async";
    image.addEventListener(
      "error",
      () => {
        image.src = fallbackImage;
        image.alt = title;
        image.classList.add("cod5-static-blog-fallback-image");
      },
      { once: true }
    );
  });
}

function addFooterEmailAndCredit(): void {
  const footer = document.querySelector<HTMLElement>("footer.elementor-location-footer, footer");
  if (!footer) return;

  if (!footer.querySelector(".cod5-footer-contact-bar")) {
    const contact = document.createElement("div");
    contact.className = "cod5-footer-contact-bar";
    contact.innerHTML = [
      '<div class="cod5-footer-contact-bar__inner">',
      '<span>Contato</span>',
      '<a href="tel:+5565992832727">(65) 99283-2727</a>',
      '<a href="mailto:contato@clinicapetterle.com.br">contato@clinicapetterle.com.br</a>',
      '</div>',
    ].join("");
    footer.appendChild(contact);
  }

  if (!footer.querySelector(".cod5-footer-credit")) {
    const credit = document.createElement("div");
    credit.className = "cod5-footer-credit";
    credit.innerHTML = [
      '<div class="cod5-footer-credit__inner">',
      '<span>Desenvolvido e Hospedado por</span>',
      '<a href="https://codigo5.com.br/?utm_source=Cl%C3%ADnica%20Petterle%20%28https%3A%2F%2Fclinicapetterle.com.br%2F%29&utm_medium=codigo5.com.br&utm_campaign=footer-credit" target="_blank" rel="nofollow noopener" aria-label="Desenvolvido pela Agência Código5">',
      '<picture>',
      '<source srcset="/codigo5-credit.webp" type="image/webp" />',
      '<img src="/codigo5-credit.jpg" alt="Código5" width="110" height="28" loading="lazy" decoding="async" />',
      '</picture>',
      '<span class="cod5-footer-credit__domain">codigo5.com.br</span>',
      '</a>',
      '</div>',
    ].join("");
    footer.appendChild(credit);
  }
}

function activateStaticEnhancements(): () => void {
  activateLazyAssets();
  normalizeStaticLinks();
  normalizeBlogImages();
  addFooterEmailAndCredit();
  const cleanupMenus = activateStaticMenus();
  scrollToHash(window.location.hash);
  return cleanupMenus;
}

export function App() {
  const [currentPath, setCurrentPath] = useState(() => normalizePath(window.location.pathname));
  const pageMeta = useMemo(() => findPageIndex(currentPath), [currentPath]);
  const [page, setPage] = useState<Page | null>(null);

  useEffect(() => {
    const onPopState = () => setCurrentPath(normalizePath(window.location.pathname));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement).closest("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return;
      const url = new URL(href, window.location.origin);
      if (!isStaticInternalHost(url.hostname)) return;
      const nextPath = normalizePath(url.pathname);
      const hasRoute = pageIndex.some((item) => item.path === nextPath) || redirects.some((item) => item.from === nextPath);
      if (!hasRoute && !url.hash) return;
      event.preventDefault();
      window.history.pushState(null, "", `${nextPath}${url.hash}`);
      if (nextPath === currentPath) {
        scrollToHash(url.hash);
      } else {
        setCurrentPath(nextPath);
        if (!url.hash) window.scrollTo({ top: 0, behavior: "instant" });
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [currentPath]);

  useEffect(() => {
    let cancelled = false;
    setPage(null);
    fetch(pageMeta.dataUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to load ${pageMeta.dataUrl}`);
        return response.json() as Promise<Page>;
      })
      .then((nextPage) => {
        if (!cancelled) setPage(nextPage);
      })
      .catch(() => {
        if (!cancelled) {
          setPage({ ...pageMeta, bodyClass: "", head: [], body: "<p>Pagina indisponivel.</p>", scripts: [] });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [pageMeta]);

  useEffect(() => {
    if (!page) return;
    const cleanupBody = setBodyClass(page);
    setHead(page);
    const cleanupMotion = activateStaticMotion();
    activateStaticCounters();
    const cleanupEnhancements = activateStaticEnhancements();
    // WordPress/Elementor scripts call admin-ajax, REST and legacy plugin assets.
    // Static interactions are handled above to keep the preview independent from the legacy origin.
    return () => {
      cleanupEnhancements();
      cleanupMotion();
      cleanupBody();
    };
  }, [page]);

  if (!page) {
    return <main className="cod5-static-wordpress-page" data-route={pageMeta.path} />;
  }

  return (
    <main
      className="cod5-static-wordpress-page"
      data-route={page.path}
      dangerouslySetInnerHTML={{ __html: page.body }}
    />
  );
}
